import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Lock, Database, Globe2, FileCheck, ArrowUpRight } from 'lucide-react';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';
import { Reveal } from '../../components/landing/Reveal';

type Status = 'live' | 'inProgress';

interface Item {
  key: string;
  icon: typeof ShieldCheck;
  status: Status;
}

/** What we ship today vs. what's on the certification roadmap — labelled honestly. */
const ITEMS: Item[] = [
  { key: 'encryption', icon: Lock, status: 'live' },
  { key: 'audit', icon: Database, status: 'live' },
  { key: 'region', icon: Globe2, status: 'live' },
  { key: 'retention', icon: FileCheck, status: 'live' },
  { key: 'soc2', icon: ShieldCheck, status: 'inProgress' },
  { key: 'isms', icon: ShieldCheck, status: 'inProgress' },
];

const LINKS: Array<{ to: string; key: string; external?: boolean }> = [
  { to: '/legal/privacy', key: 'privacy' },
  { to: '/legal/dpa', key: 'dpa' },
  { to: '/legal/subprocessors', key: 'subprocessors' },
  { to: '/status', key: 'status' },
];

export function SecurityPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        {/* Intro */}
        <section className="washi border-b border-border">
          <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
            <Reveal as="div" className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
                <span className="sparkle-dot" aria-hidden />
                {t('security.eyebrow')}
              </span>
              <h1 className="display text-4xl lg:text-5xl font-semibold tracking-tight mt-5">
                {t('security.title')}
              </h1>
              <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
                {t('security.lead')}
              </p>
            </Reveal>
          </div>
        </section>

        {/* Controls grid */}
        <section className="border-b border-border">
          <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
            <div className="grid md:grid-cols-2 gap-5">
              {ITEMS.map((it, i) => (
                <Reveal key={it.key} as="div" delay={(i % 2) * 0.06}>
                  <SecCard
                    icon={it.icon}
                    status={it.status}
                    title={t(`security.items.${it.key}Title`)}
                    desc={t(`security.items.${it.key}Desc`)}
                  />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Documents + status */}
        <section className="border-b border-border bg-card/40">
          <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
            <Reveal as="div" className="max-w-2xl">
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('security.docs.eyebrow')}
              </span>
              <h2 className="display text-2xl lg:text-3xl font-semibold mt-3">
                {t('security.docs.title')}
              </h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                {t('security.docs.body')}
              </p>
            </Reveal>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card px-5 py-4 hover:border-foreground/30 transition-colors"
                >
                  <span className="text-sm font-medium">{t(`security.docs.${l.key}`)}</span>
                  <ArrowUpRight
                    size={16}
                    className="text-muted-foreground group-hover:text-foreground transition-colors rtl-flip"
                    aria-hidden
                  />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Reach us */}
        <section className="border-b border-border">
          <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
            <Reveal as="div" className="rounded-2xl border border-border bg-card p-6 lg:p-8">
              <h2 className="display text-xl font-semibold">{t('security.reachUs')}</h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-2xl">
                {t('security.reachUsBody')}
              </p>
              <a
                href="mailto:security@plyez.com"
                className="num mt-4 inline-block text-sm text-cinnabar-600 dark:text-cinnabar-300 hover:underline"
              >
                security@plyez.com
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function SecCard({
  icon: Icon,
  title,
  desc,
  status,
}: {
  icon: typeof ShieldCheck;
  title: string;
  desc: string;
  status: Status;
}) {
  const { t } = useTranslation();
  return (
    <div className="h-full rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-lg bg-accent-50 dark:bg-accent-900/40 text-accent-700 dark:text-accent-200 flex items-center justify-center shrink-0">
          <Icon size={18} aria-hidden />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{title}</span>
            <StatusBadge status={status} label={t(`security.status.${status}`)} />
          </div>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status, label }: { status: Status; label: string }) {
  const live = status === 'live';
  return (
    <span
      className={
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] border ' +
        (live
          ? 'border-border bg-muted text-foreground/70'
          : 'border-cinnabar-200 dark:border-cinnabar-800 bg-cinnabar-50 dark:bg-cinnabar-900/30 text-cinnabar-700 dark:text-cinnabar-200')
      }
    >
      <span
        className={
          'h-1.5 w-1.5 rounded-full ' + (live ? 'bg-foreground/40' : 'bg-cinnabar-500')
        }
        aria-hidden
      />
      {label}
    </span>
  );
}
